import { REDIRECT_AFTER_PROFILE_CREATE } from '@/src/constants/common';
import { useDebounce } from '@/src/hooks/useDebounce';
import { useApi } from '@/src/store/api.context';
import { useUser } from '@/src/store/user.context';
import { CreateJamProfileFields } from '@/src/types/CreateJamProfile';
import { createJamProfileValidator } from '@/src/utils/create-jam-profile.validation.utils';
import { delay } from '@/src/utils/delay';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { localLogger } from '@/src/utils/log.utils';
import SessionStorage from '@/src/utils/session-storage';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  FormField,
  Header,
  Input,
  Link,
  SpaceBetween,
} from '@amzn/awsui-components-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import JamSpinner from '../../common/JamSpinner/JamSpinner';
import { learnerTermsAndConditionsLink, privacyNoticeLink } from '../../Profile/JamProfileCreate/jam-profile.config';

const JamProfileCreateUnifiedProfile = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { onNicknameChange } = useUser();
  const { jamProfileAPI } = useApi();
  const [displayName, setDisplayName] = useState<string>('');
  const displayNameSearch = useDebounce<string>(displayName, 300);
  // Add empty state variables for compatibility
  const [email, setEmail] = useState<string>('');

  const [displayNameError, setDisplayNameError] = useState('');
  const [displayNameUniqueError, setDisplayNameUniqueError] = useState('');
  const [loading, setLoading] = useState<boolean>(false);

  const validator = createJamProfileValidator(
    displayName,
    t(i18nKeys.createJamProfile.form.validation.displayNameError),
    new Map<CreateJamProfileFields, (error: string) => void>([
      [CreateJamProfileFields.DISPLAY_NAME, (error: string) => setDisplayNameError(error)],
    ])
  );

  const checkDisplayNameUnique = async () => {
    await delay(300);
    if (displayNameSearch.length <= 3) {
      setDisplayNameUniqueError(t(i18nKeys.createJamProfile.form.validation.displayNameShortError));
      return;
    } else {
      setDisplayNameUniqueError('');
    }
  };

  const onSubmit = async () => {
    const isValid = validator.isValidSection(true);
    if (!isValid || displayNameUniqueError) {
      return;
    }

    const body = {
      nickname: displayName,
      email,
    };

    setLoading(true);
    try {
      await jamProfileAPI.updateProfile(body);
      onNicknameChange(displayName);

      const redirectAfterProfileCreate = SessionStorage.get(REDIRECT_AFTER_PROFILE_CREATE) as Location;

      if (redirectAfterProfileCreate) {
        history.replace(redirectAfterProfileCreate);
      } else {
        history.replace('/');
      }
    } catch (error) {
      localLogger('error', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!displayNameSearch) {
      return;
    }
    void checkDisplayNameUnique();
  }, [displayNameSearch]);

  if (loading) {
    return <JamSpinner />;
  }

  return (
    <ContentLayout
      header={<Header description={t(i18nKeys.createJamProfile.caption)}>{t(i18nKeys.createJamProfile.title)}</Header>}
    >
      <div className="jam-profile-create-container">
        <Container header={<Header variant="h3">{t(i18nKeys.createJamProfile.profileDetails)}</Header>}>
          <form onSubmit={(e) => e.preventDefault()}>
            <SpaceBetween size="l">
              <FormField
                label={`${t(i18nKeys.createJamProfile.form.displayName)}*`}
                description={t(i18nKeys.createJamProfile.form.displayNameDesc)}
                i18nStrings={{ errorIconAriaLabel: t(i18nKeys.general.error) }}
                errorText={displayNameUniqueError || displayNameError}
              >
                <Input
                  id="displayNameTextBox"
                  value={displayName}
                  onChange={({ detail }) => setDisplayName(detail.value)}
                  placeholder={t(i18nKeys.createJamProfile.form.displayNamePlaceholder)}
                  onBlur={() => validator.isValidField(CreateJamProfileFields.DISPLAY_NAME)}
                />
              </FormField>
            </SpaceBetween>
          </form>
        </Container>
        <div className="final-note">
          <Box variant="p" fontSize="body-s" margin={{ top: 'l', bottom: 'm' }}>
            {t(i18nKeys.createJamProfile.termsDesc)}
            <Link fontSize="inherit" href={learnerTermsAndConditionsLink} target="blank">
              {t(i18nKeys.createJamProfile.termLink)}
            </Link>
            .
            <br />
            {t(i18nKeys.createJamProfile.privacyDesc)}
            <Link fontSize="inherit" href={privacyNoticeLink} target="blank">
              {t(i18nKeys.createJamProfile.privacyLink)}
            </Link>
            .
          </Box>
          <Box float="right">
            <Button
              variant="primary"
              id="createJamProfileButton"
              data-testid="createJamProfileButton"
              onClick={() => void onSubmit()}
            >
              {t(i18nKeys.createJamProfile.form.submitButton)}
            </Button>
          </Box>
        </div>
      </div>
    </ContentLayout>
  );
};

export default JamProfileCreateUnifiedProfile;
